import React from 'react';
import styled, { keyframes } from 'styled-components';
import { StoryList } from '../components/StoryList';
import { Button } from '../components/common/Button';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../constants/colors';
import { PersonalizedGreeting } from '../components/PersonalizedGreeting';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const LibraryContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 24px;
  animation: ${fadeIn} 0.4s ease-out;
`;

const LibraryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 36px;
  flex-wrap: wrap;
  gap: 16px;
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 6px 0;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  font-size: 1.05rem;
  color: #6c757d;
  margin: 0;
  line-height: 1.5;
`;

const CreateButton = styled(Button)`
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  }
`;

export const StoryLibraryPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoggedIn } = useAuth();
  const { t } = useTranslation();

  // Set by the homepage "print your stories" card so visitors land
  // directly in selection mode instead of having to find the book button.
  const startBookSelection = Boolean(location.state?.startBookSelection);

  return (
    <LibraryContainer>
      <LibraryHeader>
        <HeaderContent>
          {isLoggedIn ? (
            <PersonalizedGreeting />
          ) : (
            <>
              <Title>{t('library.title')}</Title>
              <Subtitle>{t('library.subtitle')}</Subtitle>
            </>
          )}
        </HeaderContent>
        
        {isLoggedIn && (
          <CreateButton
            variant="primary"
            onClick={() => navigate('/create')}
          >
            {t('library.createButton')}
          </CreateButton>
        )}
      </LibraryHeader>
      
      <StoryList
        showFilters={true}
        userId={user?.id}
        autoStartBookSelection={startBookSelection}
      />
    </LibraryContainer>
  );
};